import { FormControl, FormGroup, Validators } from '@angular/forms';
import {
  GoalKeyInterface,
  NamespaceInterface,
  SituationMappingInterface,
  SituationConfigInterface,
} from '@vendasta/ai-assistants';
import { FormArrayWithDirtyTracking } from './forms';

export interface ForkedCapabilityMapping {
  oldKey: GoalKeyInterface;
  newKey: GoalKeyInterface;
}

function areNamespacesEqual(
  ns1: NamespaceInterface | null | undefined,
  ns2: NamespaceInterface | null | undefined,
): boolean {
  if (!ns1 && !ns2) return true;
  if (!ns1 || !ns2) return false;
  return (
    ns1.accountGroupNamespace?.accountGroupId === ns2.accountGroupNamespace?.accountGroupId &&
    ns1.partnerNamespace?.partnerId === ns2.partnerNamespace?.partnerId &&
    !!ns1.systemNamespace === !!ns2.systemNamespace &&
    !!ns1.globalNamespace === !!ns2.globalNamespace
  );
}

export function areGoalKeysEqual(
  key1: GoalKeyInterface | null | undefined,
  key2: GoalKeyInterface | null | undefined,
): boolean {
  if (!key1 || !key2) return false;
  if (key1.id !== key2.id) return false;
  return areNamespacesEqual(key1.namespace, key2.namespace);
}

export type AiSituationMappingFormControls = {
  situation: FormControl<string | null>;
  goalKeys: FormControl<GoalKeyInterface[] | null>;
};

export class AiSituationMappingForm extends FormGroup<AiSituationMappingFormControls> {
  constructor(situationMapping?: { situation?: string; goalKeys?: GoalKeyInterface[] }) {
    super({
      situation: new FormControl(situationMapping?.situation ?? null, Validators.required),
      goalKeys: new FormControl(situationMapping?.goalKeys ?? [], Validators.required),
    });
  }

  toSituationMapping(): SituationMappingInterface {
    return {
      situation: this.controls.situation.value ?? '',
      goalKeys: this.controls.goalKeys.value ?? [],
    };
  }
}

export class AiSituationMappingFormArray extends FormArrayWithDirtyTracking<AiSituationMappingForm> {
  constructor(situationMappings?: Array<{ situation?: string; goalKeys?: GoalKeyInterface[] }>) {
    super((situationMappings ?? []).map((mapping) => new AiSituationMappingForm(mapping)));
  }

  toSituationMappings(): SituationMappingInterface[] {
    return this.controls.map((mappingForm) => mappingForm.toSituationMapping());
  }
}

export type AiSituationConfigFormControls = {
  situationMappings: AiSituationMappingFormArray;
};

export class AiSituationConfigForm extends FormGroup<AiSituationConfigFormControls> {
  constructor(situationConfig?: { situationMappings?: Array<{ situation?: string; goalKeys?: GoalKeyInterface[] }> }) {
    super({
      situationMappings: new AiSituationMappingFormArray(situationConfig?.situationMappings),
    });
  }

  toSituationConfig(): SituationConfigInterface {
    return {
      simpleConfig: this.controls.situationMappings.toSituationMappings(),
    };
  }

  updateForkedCapabilityReferences(forkedMappings: ForkedCapabilityMapping[]): void {
    for (const mapping of this.controls.situationMappings.controls) {
      const currentGoalKeys = mapping.controls.goalKeys.value ?? [];
      let updated = false;
      const updatedGoalKeys = currentGoalKeys.map((key) => {
        const forkedMapping = forkedMappings.find((m) => areGoalKeysEqual(m.oldKey, key));
        if (forkedMapping) {
          updated = true;
          return forkedMapping.newKey;
        }
        return key;
      });
      if (updated) {
        mapping.controls.goalKeys.setValue(updatedGoalKeys, { emitEvent: false });
        mapping.controls.goalKeys.markAsDirty({ onlySelf: true });
      }
    }
  }

  getSituationsReferencingCapability(capabilityKey: GoalKeyInterface | null): string[] {
    return this.controls.situationMappings.controls
      .filter((mapping) => {
        const goalKeys = mapping.controls.goalKeys.value ?? [];
        return goalKeys.some((key) => areGoalKeysEqual(key, capabilityKey));
      })
      .map((mapping) => mapping.controls.situation.value ?? '');
  }

  removeCapabilityFromMappings(capabilityKey: GoalKeyInterface | null): void {
    const mappingsToRemove: number[] = [];

    this.controls.situationMappings.controls.forEach((mapping, index) => {
      const currentGoalKeys = mapping.controls.goalKeys.value ?? [];
      const filteredGoalKeys = currentGoalKeys.filter((key) => !areGoalKeysEqual(key, capabilityKey));

      if (filteredGoalKeys.length === 0) {
        // remove situation if it no longer has any capabilities
        mappingsToRemove.push(index);
      } else if (filteredGoalKeys.length !== currentGoalKeys.length) {
        mapping.controls.goalKeys.setValue(filteredGoalKeys);
        mapping.controls.goalKeys.markAsDirty();
      }
    });

    for (const index of mappingsToRemove.reverse()) {
      this.controls.situationMappings.removeAt(index);
      this.controls.situationMappings.markAsDirty();
    }
  }
}
